import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { IMessage } from 'react-native-gifted-chat';
import { v4 as uuidv4 } from 'uuid';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import {
  clearHandOff,
  OPEN_REPLY_DRAFTS_QUERY_KEY,
  readHandOffLink,
} from '@/features/reply-drafts/state';
import { useHaptic } from '@/hooks/useHaptic';
import { useToaster } from '@/hooks/useToaster';
import type { ChatConversation, OutgoingChatMessage } from '@/services/chat/chatTransport';
import { useOutboxStore } from '@/services/chat/outboxStore';
import { createUploadUrlV2 } from '@/services/createUploadUrlV2';
import { uploadFileToPresignedUrl } from '@/services/uploadFileToPresignedUrl';
import { createExpertRelayMessageData, createPrimumSupportMessageData } from '@/types/messageData';
import type { UploadFile } from '@/types/uploadFile';
import { getFileContent } from '@/util/getFileContent';
import { trackEvent } from '@/util/trackEvent';

type ToastClient = Pick<ReturnType<typeof useToaster>, 'showError'>;

type HapticClient = Pick<ReturnType<typeof useHaptic>, 'impactLight'>;

/**
 * Per-send attribution override. When the caller decides expert-vs-Primum at the
 * moment of sending (inline send prompt) rather than via sticky config, it passes
 * the choice here so it isn't lost to a stale render-time closure.
 */
export type SendMessageAttribution = {
  sendAsExpertRelay?: boolean;
  sendAsPrimumSupport?: boolean;
  /** Per-send override for the relayed expert's display name. Only applied when sending as expert relay. */
  expertName?: string;
};

function getMessageData({
  sendAsExpertRelay,
  sendAsPrimumSupport,
  expertName,
}: {
  sendAsExpertRelay: boolean;
  sendAsPrimumSupport: boolean;
  expertName?: string;
}) {
  if (sendAsExpertRelay) {
    return createExpertRelayMessageData(expertName);
  }

  if (sendAsPrimumSupport) {
    return createPrimumSupportMessageData();
  }

  return undefined;
}

/**
 * How many times a send that never reached the server is tried again on its
 * own, under the same id. Only for a conversation whose sends are idempotent.
 */
const AUTOMATIC_RETRIES = 2;

/**
 * True when the send never got an answer: `fetch` rejects with a `TypeError`
 * when no response arrived, which is the rule support threads retry by. Any
 * answer, even a 500, means the server saw the request, and is left to the
 * reader's Retry.
 */
function isUnanswered(error: unknown): boolean {
  return error instanceof TypeError;
}

type EventProperties = NonNullable<Parameters<typeof trackEvent>[1]>;

type UseChatSendMessageParams = {
  clearDraft: () => Promise<void> | void;
  conversation: ChatConversation;
  files: UploadFile[];
  inquiryGroupsEnabled?: boolean;
  inquiryGroupSize?: number;
  hasFiles: boolean;
  haptic: HapticClient;
  inquiryId: number;
  inquiryParentId?: number | null;
  isPrimumAnonymousRelayConversation?: boolean;
  isMaintenanceMode: boolean;
  noPHIConfirmed: boolean;
  resetAttachments: () => void;
  setDraftMessage: (message: string) => void;
  toast: ToastClient;
  /** When true, POST includes expert-relay metadata for Twilio attributes (anonymous inquiries). */
  sendAsExpertRelay?: boolean;
  /** When true, POST includes Primum Support metadata for Twilio attributes. */
  sendAsPrimumSupport?: boolean;
};

/**
 * Sends the composer's message through the conversation.
 *
 * How a send looks follows what the conversation guarantees. Where a repeat
 * of the same `clientMessageId` cannot post twice, the message shows at once
 * as pending, the composer clears, a send that got no answer is tried again
 * on its own, and one that still fails stays in the thread with Retry and
 * Delete, as in a support thread. Otherwise, which is every transport until
 * Primum stores inquiry messages, the composer holds the text with "Sending
 * message..." until the server answers and hands it back on a failure,
 * because a retry after a send that did land would post it twice.
 */
export function useChatSendMessage({
  clearDraft,
  conversation,
  files,
  inquiryGroupsEnabled = false,
  inquiryGroupSize,
  hasFiles,
  haptic,
  inquiryId,
  inquiryParentId,
  isPrimumAnonymousRelayConversation = false,
  isMaintenanceMode,
  noPHIConfirmed,
  resetAttachments,
  setDraftMessage,
  toast,
  sendAsExpertRelay = false,
  sendAsPrimumSupport = false,
}: UseChatSendMessageParams) {
  const queryClient = useQueryClient();

  const conversationId = conversation.id;

  const [isSending, setIsSending] = useState(false);

  const [sendingStatus, setSendingStatus] = useState('');

  /**
   * Synchronous in-flight guard. The `isSending` state flips asynchronously,
   * so a rapid burst of clicks all see `disabled={!isSending}` as false and
   * each enqueues a send. A ref updated synchronously rejects re-entry
   * before React has a chance to render the disabled state.
   */
  const isSendInFlightRef = useRef(false);

  const { mutateAsync: sendThroughConversation } = useMutation({
    mutationFn: (message: OutgoingChatMessage) => conversation.send(message),
  });

  /** The analytics for each pending send, kept so a retry reports what the first try would have. */
  const pendingEvents = useRef(
    new Map<string, { sent: EventProperties; failed: EventProperties }>()
  );

  /** What follows a send the server accepted, in either mode. */
  const onSent = useCallback(
    (sentProperties: EventProperties) => {
      /**
       * Only the server knows whether this message used, kept or discarded
       * the expert's draft, so the open drafts are re-read after every send.
       */
      queryClient
        .invalidateQueries({ queryKey: OPEN_REPLY_DRAFTS_QUERY_KEY })
        .catch(() => undefined);

      trackEvent(ANALYTICS_EVENTS.MESSAGE_SENT, sentProperties);
    },
    [queryClient]
  );

  /**
   * Sends a pending message, trying again on its own while the send gets no
   * answer. Safe only because the conversation's sends are idempotent. A
   * refusal, or a send still unanswered after the retries, leaves it failed.
   */
  const deliver = useCallback(
    async (message: OutgoingChatMessage) => {
      const events = pendingEvents.current.get(message.clientMessageId);

      for (let attempt = 0; ; attempt += 1) {
        try {
          await conversation.send(message);

          break;
        } catch (error) {
          if (attempt < AUTOMATIC_RETRIES && isUnanswered(error)) {
            continue;
          }

          useOutboxStore.getState().setStatus(conversationId, message.clientMessageId, 'failed');

          trackEvent(ANALYTICS_EVENTS.MESSAGE_SEND_FAILED, {
            ...events?.failed,
            error: error ? JSON.stringify(error) : 'Unknown error',
          });

          return;
        }
      }

      pendingEvents.current.delete(message.clientMessageId);

      onSent(events?.sent ?? {});
    },
    [conversation, conversationId, onSent]
  );

  /** Sends a failed message again, under the id it was first sent with. */
  const retryMessage = useCallback(
    (clientMessageId: string) => {
      const entry = useOutboxStore
        .getState()
        .entries[
          conversationId
        ]?.find((candidate) => candidate.message.clientMessageId === clientMessageId);

      if (!entry || entry.status !== 'failed') {
        return;
      }

      useOutboxStore.getState().setStatus(conversationId, clientMessageId, 'pending');

      deliver(entry.message).catch(() => undefined);
    },
    [conversationId, deliver]
  );

  /** Drops a failed message the reader no longer wants sent. */
  const discardMessage = useCallback(
    (clientMessageId: string) => {
      pendingEvents.current.delete(clientMessageId);

      useOutboxStore.getState().remove(conversationId, clientMessageId);
    },
    [conversationId]
  );

  const sendMessage = useCallback(
    async (newMessages: IMessage[], attribution?: SendMessageAttribution) => {
      if (isSendInFlightRef.current) {
        return;
      }

      isSendInFlightRef.current = true;

      const resolvedExpertRelay = attribution?.sendAsExpertRelay ?? sendAsExpertRelay;

      const resolvedPrimumSupport = attribution?.sendAsPrimumSupport ?? sendAsPrimumSupport;

      const messageData = getMessageData({
        sendAsExpertRelay: resolvedExpertRelay,
        sendAsPrimumSupport: resolvedPrimumSupport,
        expertName: attribution?.expertName,
      });

      if (isMaintenanceMode) {
        toast.showError(ERROR_MESSAGES.MESSAGES.MAINTENANCE_MODE);

        isSendInFlightRef.current = false;

        return;
      }

      haptic.impactLight();

      try {
        const text = newMessages[0].text.trim();

        if (hasFiles && !noPHIConfirmed) {
          toast.showError({
            title: 'PHI confirmation required',
            message: 'Please confirm your attachments contain no PHI before sending.',
          });

          return;
        }

        setIsSending(true);

        setDraftMessage(text);

        const filesToSend: { s3Key: string; fileName: string; noPHI: true }[] = [];

        if (files.length > 0) {
          let currentFile: UploadFile | undefined;

          try {
            for (let index = 0; index < files.length; index++) {
              const file = files[index];

              currentFile = file;

              setSendingStatus(`Uploading attachments (${index + 1}/${files.length}) ...`);

              const uploadUrlData = await createUploadUrlV2({
                mimeType: file.mimeType as any,
              });

              if (!uploadUrlData?.url || !uploadUrlData?.s3Key) {
                throw new Error(`Failed to get upload URL for ${file.name}`);
              }

              const fileContent = await getFileContent(file);

              await uploadFileToPresignedUrl({
                presignedUrl: uploadUrlData.url,
                file: fileContent,
              });

              filesToSend.push({
                s3Key: uploadUrlData.s3Key,
                fileName: file.name,
                noPHI: true,
              });

              trackEvent(ANALYTICS_EVENTS.FILE_UPLOAD_COMPLETED, {
                file_name: file.name,
                file_size: file.size,
                mime_type: file.mimeType,
                is_video: file.mimeType.startsWith('video/'),
                inquiry_id: inquiryId,
                conversation_id: conversationId,
                context: 'chat_message',
              });
            }
          } catch (error) {
            console.error('File upload failed:', error);

            toast.showError({
              title: 'File upload failed',
              message: ERROR_MESSAGES.ATTACHMENTS.UPLOAD_FAILED.CHAT,
            });

            trackEvent(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
              error: error instanceof Error ? error.message : 'Unknown error',
              file_size: currentFile?.size,
              mime_type: currentFile?.mimeType,
              inquiry_id: inquiryId,
              conversation_id: conversationId,
              context: 'chat_message',
            });

            return;
          }
        }

        /**
         * A reply handed over from a reply draft names it, so the server can
         * link the message to the draft. The link outlives an app restart and
         * is read here, at send, rather than captured when the text was set.
         */
        const handOff = readHandOffLink(conversationId);

        const outgoing: OutgoingChatMessage = {
          clientMessageId: uuidv4(),
          body: text,
          files: filesToSend,
          ...(messageData ? { data: messageData } : {}),
          ...(handOff ? { replyDraftId: handOff.replyDraftId } : {}),
        };

        const sentProperties: EventProperties = {
          inquiry_id: conversationId,
          inquiryId,
          conversationId,
          inquiryGroupsEnabled,
          inquiryParentId: inquiryParentId ?? null,
          inquiryGroupSize: inquiryGroupSize ?? 1,
          isGroupedInquiry: (inquiryGroupSize ?? 1) > 1,
          isPrimumAnonymousRelayConversation,
          message: text,
          contains_files: !!filesToSend.length,
          files: filesToSend,
          messageDataType: messageData?.type ?? 'plain_text',
          sendAsExpertRelay: resolvedExpertRelay,
          sendAsPrimumSupport: resolvedPrimumSupport,
        };

        if (conversation.capabilities.idempotentSend) {
          pendingEvents.current.set(outgoing.clientMessageId, {
            sent: sentProperties,
            failed: {
              inquiry_id: conversationId,
              inquiryId,
              conversationId,
              isPrimumAnonymousRelayConversation,
              sendAsExpertRelay: resolvedExpertRelay,
              sendAsPrimumSupport: resolvedPrimumSupport,
              messageDataType: messageData?.type ?? 'plain_text',
            },
          });

          useOutboxStore.getState().put({
            conversationId,
            message: outgoing,
            createdAt: new Date(),
            status: 'pending',
          });

          // The pending message carries the draft's id, so a retry still names it.
          clearHandOff(conversationId);

          Keyboard.dismiss();

          await clearDraft();

          resetAttachments();

          setSendingStatus('');

          deliver(outgoing).catch(() => undefined);

          return;
        }

        setSendingStatus('Sending message...');

        await sendThroughConversation(outgoing);

        clearHandOff(conversationId);

        onSent(sentProperties);

        Keyboard.dismiss();

        await clearDraft();

        resetAttachments();

        setSendingStatus('');
      } catch (error) {
        trackEvent(ANALYTICS_EVENTS.MESSAGE_SEND_FAILED, {
          inquiry_id: conversationId,
          inquiryId,
          conversationId,
          error: error ? JSON.stringify(error) : 'Unknown error',
          isPrimumAnonymousRelayConversation,
          sendAsExpertRelay: resolvedExpertRelay,
          sendAsPrimumSupport: resolvedPrimumSupport,
          messageDataType: messageData?.type ?? 'plain_text',
        });

        toast.showError({
          title: 'Unable to send message',
        });
      } finally {
        setIsSending(false);

        setSendingStatus('');

        isSendInFlightRef.current = false;
      }
    },
    [
      clearDraft,
      conversation,
      conversationId,
      deliver,
      onSent,
      files,
      inquiryGroupSize,
      inquiryGroupsEnabled,
      hasFiles,
      haptic,
      inquiryId,
      inquiryParentId,
      isPrimumAnonymousRelayConversation,
      isMaintenanceMode,
      noPHIConfirmed,
      resetAttachments,
      sendThroughConversation,
      setDraftMessage,
      toast,
      sendAsExpertRelay,
      sendAsPrimumSupport,
    ]
  );

  return {
    isSending,
    sendMessage,
    sendingStatus,
    retryMessage,
    discardMessage,
  };
}
